import React, { Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/react";
import { useSyncSignedInUser } from "./api";
import { GalleryPage } from "./GalleryPage";
import { HomePage } from "./HomePage";
import { PhotoPage } from "./PhotoPage";
import { UploadPage } from "./UploadPage";
import { LoadingView } from "./ui";
import { useGalleryNavigation } from "./useGalleryNavigation";
import "./styles.css";
import { AccountAppearanceProvider } from "./AccountAppearance";

const EditorPage = lazy(() => import("./EditorPage").then((module) => ({ default: module.EditorPage })));
const SettingsPage = lazy(() => import("./SettingsPage").then((module) => ({ default: module.SettingsPage })));

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const LocalLandingPreview = lazy(() => import("./LocalLandingPreview"));
if (!clerkPublishableKey && !import.meta.env.DEV) throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");

function App() {
  useSyncSignedInUser();
  const { pathname, initialData, openingSlug, openGallery } = useGalleryNavigation();
  const route = parseRoute(pathname);
  if (route.name === "home") return <HomePage onOpenGallery={openGallery} openingSlug={openingSlug} />;
  if (route.name === "gallery") return <GalleryPage key={route.slug} slug={route.slug} initialData={initialData} />;
  if (route.name === "upload") return <UploadPage slug={route.slug} />;
  if (route.name === "settings") return <Suspense fallback={<LoadingView />}><SettingsPage slug={route.slug} /></Suspense>;
  if (route.name === "editor") return <Suspense fallback={<LoadingView />}><EditorPage slug={route.slug} photoId={route.photoId} /></Suspense>;
  if (route.name === "photo") return <PhotoPage slug={route.slug} photoId={route.photoId} />;
  return <main className="center-shell"><p>Page not found.</p><a className="link-button" href="/">Home</a></main>;
}

function parseRoute(path: string) {
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 0) return { name: "home" as const };
  if (parts[0] === "g" && parts[1] && parts.length === 2) return { name: "gallery" as const, slug: parts[1] };
  if (parts[0] === "g" && parts[1] && parts[2] === "upload") return { name: "upload" as const, slug: parts[1] };
  if (parts[0] === "settings" && parts[1]) return { name: "settings" as const, slug: parts[1] };
  if (parts[0] === "g" && parts[1] && parts[2] === "photo" && parts[3] && parts[4] === "edit") {
    return { name: "editor" as const, slug: parts[1], photoId: parts[3] };
  }
  if (parts[0] === "g" && parts[1] && parts[2] === "photo" && parts[3] && parts.length === 4) {
    return { name: "photo" as const, slug: parts[1], photoId: parts[3] };
  }
  return { name: "notFound" as const };
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {clerkPublishableKey ? <ClerkProvider publishableKey={clerkPublishableKey} afterSignOutUrl="/">
      <AccountAppearanceProvider><App /></AccountAppearanceProvider>
    </ClerkProvider> : <Suspense fallback={<LoadingView />}><LocalLandingPreview /></Suspense>}
  </React.StrictMode>
);
